import React, { Fragment, useEffect, useState } from 'react'

// Merged imports to be cleaner
import { BarChartComponent, PieChartComponent, LoadingSpinner } from '../components'
import { API_BASE_URL, EU_EVENT_IDS, UK_EVENT_IDS } from '../data'
import {
    VolunteerOverviewMetaProps,
    Area,
    Shift,
    Event,
    CategoryType,
    GroupedDataType
} from '../data/types'
import { VolunteerViewModal } from '../components/mangement_information/VolunteerViewModal'
import { AVAILABILITY_OPTIONS } from '../data/meta'

type Props = {
    meta: VolunteerOverviewMetaProps,
    event?: Event,
    area?: Area,
    shift?: Shift
    iff?: boolean
    non_iiuk?: boolean
    checkin?: boolean
}



export const VolunteerOverview = ({ meta, event, area, shift, iff, non_iiuk, checkin }: Props) => {

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [euGroupedData, setEUGroupData] = useState<GroupedDataType | null>(null)
    const [ukGroupedData, setUKGroupData] = useState<GroupedDataType | null>(null)
    const [isVolunteerViewModalOpen, setIsVolunteerViewModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryType | null>(null);

    const uniformedVolunteerTitles = ['Volunteer', 'Deputy Director', 'Team Leader', 'Director', 'Uniformed Volunteer', 'IV Events', 'IV Executive'];
    const nonUniformedVolunteerTitles = ['Non-Uniformed Volunteer', 'Other'];


    const buildQueryString = (country?: string) => {
        const params = new URLSearchParams()

        if (iff)
            params.append('iff', "1");

        if (non_iiuk)
            params.append('non_iiuk', "1");

        if (checkin)
            params.append('checkin', "1");

        if (shift)
            params.append('shift_id', shift.id.toString());
        else if (area)
            params.append('area_id', area.id.toString());
        else if (event)
            params.append('event_id', event.id.toString());

        EU_EVENT_IDS.forEach(id => params.append('event_ids[]', id.toString()));

        // Sender filter for only Europe Countries
        if (country)
            params.append('country', country);
        else
            params.append('country', 'eu');

        const fields = ['age_band', 'gender', 'family_origin', 'language', 'jamat_khana', 'preference', 'flag', 'service_record', 'availability', 'country', 'family_origin'];

        if ((iff === null || iff === false) && (non_iiuk === null || non_iiuk === false))
            fields.push('service_record');

        fields.forEach(f => params.append('fields[]', f));

        return params.toString()
    }

    useEffect(() => {
        const fetchGroupStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const ukQueryString = buildQueryString('uk_ireland')
                const euQueryString = buildQueryString();

                const [euResponse, ukResponse] = await Promise.all([
                    fetch(`${API_BASE_URL}/dashboard/stats/volunteer?${euQueryString}`, { credentials: 'include' }),
                    fetch(`${API_BASE_URL}/dashboard/stats/volunteer?${ukQueryString}`, { credentials: 'include' })
                ]);

                if (!euResponse.ok) {
                    throw new Error(`Failed to fetch Europe statistics.`)
                }

                if (!ukResponse.ok) {
                    throw new Error(`Failed to fetch UK statistics.`)
                }

                const euResult = await euResponse.json();
                const ukResult = await ukResponse.json();

                setEUGroupData(euResult);
                setUKGroupData(ukResult);

            } catch (err) {
                setError(err instanceof Error ? err.message : "Error while fetching stats.")
            }
            finally {
                setLoading(false);
            }
        }
        fetchGroupStats();

        // FIX: Depend on IDs, not objects, to prevent infinite re-fetching loops
    }, [event?.id, area?.id, shift?.id, iff, non_iiuk])

    const handleCategoryClick = (country: string) => (category: CategoryType) => {
        setIsVolunteerViewModalOpen(true)
        setSelectedCategory({ ...category, country })
    }

    const handleCategoryClickClose = () => {
        setSelectedCategory(null);
        setIsVolunteerViewModalOpen(false);
    }


    const aggregateServiceRecordStats = (data: Record<string, string | number>[]) => {
        let uniformedStats = 0;
        let nonUniformedStats = 0;
        let juniorVolunteerStats = 0;

        data.forEach((value) => {
            if (uniformedVolunteerTitles.includes(value["name"] as string)) uniformedStats += value["count"] as number;
            else if (nonUniformedVolunteerTitles.includes(value["name"] as string)) nonUniformedStats += value["count"] as number;
        });

        return [
            {
                "name": "Uniformed Volunteer",
                "count": uniformedStats
            },
            {
                "name": "Non-Uniformed Volunteer",
                "count": nonUniformedStats
            },
            {
                "name": "Junior Volunteer",
                "count": juniorVolunteerStats
            },
        ]

    }

    return (
        <Fragment>
            <div className="flex-1 container mx-auto">
                <div className="mb-8">
                    <header className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            {meta.description}
                        </p>
                    </header>
                    <div className="mt-4">

                        {loading && <LoadingSpinner meta={{ loadingText: meta.loadingText }} />}

                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                    {meta.errorTitle}
                                </h3>
                                <p className="text-red-600 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        {!loading && !error && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-8 gap-6">

                                {/* Age Band – Pie */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-2'>
                                    <div className='flex gap-2'>
                                        <PieChartComponent
                                            title="EU - Age Band"
                                            data={euGroupedData ? euGroupedData['age_band'] : []}
                                            groupBy="age_band"
                                            width="xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <PieChartComponent
                                            title="UK - Age Band"
                                            data={ukGroupedData ? ukGroupedData['age_band'] : []}
                                            groupBy="age_band"
                                            width="xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* Gender – Pie */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-2'>
                                    <div className="flex gap-2">
                                        <PieChartComponent
                                            title="EU - Gender Distribution"
                                            data={euGroupedData ? euGroupedData['gender'] : []}
                                            groupBy="gender"
                                            width="xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <PieChartComponent
                                            title="UK - Gender Distribution"
                                            data={ukGroupedData ? ukGroupedData['gender'] : []}
                                            groupBy="gender"
                                            width="xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* Family Origin – Pie */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-2'>
                                    <div className="flex gap-2">
                                        <PieChartComponent
                                            title="EU - Family Origin"
                                            data={euGroupedData ? euGroupedData['family_origin'] : []}
                                            groupBy="family_origin"
                                            width="xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <PieChartComponent
                                            title="UK- Family Origin"
                                            data={ukGroupedData ? ukGroupedData['family_origin'] : []}
                                            groupBy="family_origin"
                                            width="xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* Jamat Khana */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-1'>
                                    <div className="flex flex-col gap-1">
                                        <BarChartComponent
                                            title="EU - Jamatkhana"
                                            data={euGroupedData ? euGroupedData['jamat_khana'] : []}
                                            groupBy='jamat_khana'
                                            height="3xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <BarChartComponent
                                            title="UK - Jamatkhana"
                                            data={ukGroupedData ? ukGroupedData['jamat_khana'] : []}
                                            groupBy='jamat_khana'
                                            height="3xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* IV Positions */}
                                {(iff === null || iff === false || iff === undefined) && (
                                    <div className='bg-gray-100 rounded-lg xl:col-span-8 p-1'>
                                        <div className="flex flex-col gap-1">
                                            <BarChartComponent
                                                title="EU - IV Positions"
                                                data={euGroupedData ? aggregateServiceRecordStats(euGroupedData['service_record'] as Record<string, string | number>[]) : []}
                                                groupBy='service_record'
                                                height="xl"
                                                width="4xl"
                                                onChartClick={handleCategoryClick('eu')}
                                            />
                                            <BarChartComponent
                                                title="UK - IV Positions"
                                                data={ukGroupedData ? aggregateServiceRecordStats(ukGroupedData['service_record'] as Record<string, string | number>[]) : []}
                                                groupBy='service_record'
                                                height="xl"
                                                width="4xl"
                                                onChartClick={handleCategoryClick('uk_ireland')}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Availibility */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-1'>
                                    <div className="flex flex-col gap-1">
                                        <BarChartComponent
                                            title="EU - Volunteer Availability"
                                            data={euGroupedData ? euGroupedData['availability'].filter(data => AVAILABILITY_OPTIONS.includes(data['name'])) : []}
                                            groupBy='availability'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <BarChartComponent
                                            title="UK - Volunteer Availability"
                                            data={ukGroupedData ? ukGroupedData['availability'].filter(data => AVAILABILITY_OPTIONS.includes(data['name'])) : []}
                                            groupBy='availability'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* Country */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-1'>
                                    <div className="flex flex-col gap-1">
                                        <BarChartComponent
                                            title="EU - Country"
                                            data={euGroupedData ? euGroupedData['country'] : []}
                                            groupBy='country'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <BarChartComponent
                                            title="UK - Country"
                                            data={ukGroupedData ? ukGroupedData['country'] : []}
                                            groupBy='country'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                                {/* Family Origin */}
                                <div className='bg-gray-100 rounded-lg xl:col-span-8 p-1'>
                                    <div className="flex flex-col gap-1">
                                        <BarChartComponent
                                            title="EU - Family Origin"
                                            data={euGroupedData ? euGroupedData['family_origin'] : []}
                                            groupBy='family_origin'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('eu')}
                                        />
                                        <BarChartComponent
                                            title="UK - Family Origin"
                                            data={ukGroupedData ? ukGroupedData['family_origin'] : []}
                                            groupBy='family_origin'
                                            height="xl"
                                            width="4xl"
                                            onChartClick={handleCategoryClick('uk_ireland')}
                                        />
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            </div>

            <VolunteerViewModal
                title={`Volunteers for ${selectedCategory?.field ?? ''} : ${selectedCategory?.value ?? ''}`}
                isOpen={isVolunteerViewModalOpen}
                onCancel={handleCategoryClickClose}
                field={selectedCategory?.field ?? ''}
                value={selectedCategory?.value ?? ''}
                event={event}
                area={area}
                shift={shift}
                iff={iff}
                non_iiuk={non_iiuk}
                checkin={checkin}
                country={selectedCategory?.country ?? 'eu'}
            />
        </Fragment>
    )
}
